// Hoist-related helpers ported from trusstor_dashboard.
// Sources:
//   trusstor_dashboard/shared/constants/professions.constants.ts
//   trusstor_dashboard/frontend/src/utils/dateUtils.ts (msToHoistTimeString)
//   trusstor_dashboard/frontend/src/Components/Hoists/utils/hoist.utils.ts
//
// We intentionally drop trusstor's `getHoistTimePastFromStartDay` /
// `isWorkDayOver` branches: those depend on `IProject.workDayHours` and
// `tz`, which d-orca doesn't surface yet. The remaining "hoist not
// available" path falls back to plain elapsed time, which is the common
// case anyway.

import type { IMergedFloorView, IMinimizedProfession } from '@d-orca/shared/trusstor';

/**
 * Trade id that identifies the hoist operator profession in the upstream
 * Trusstor catalogue. Used to (a) detect whether the operator is currently
 * inside the hoist (`isOperatorInHoist`) and (b) exclude that profession
 * from the crew chip list rendered in the hoist hover tooltip — it's
 * already represented separately by the operator status row.
 *
 * Mirror of `trusstor_dashboard/shared/constants/professions.constants.ts:9`.
 */
export const HOIST_OPERATOR_TRADE_ID = 'hoistOperator';

/**
 * Trusstor's hoist clock formatter: `Xh Ym` when the duration crosses an
 * hour, otherwise `N minute(s)`. Plural forms are simple English fall-backs;
 * the i18n catalogue can override via `t('monitor.buildingView.hour' | 'hours' | 'minute' | 'minutes')`.
 */
export function msToHoistTimeString(durationMs: number, t: (key: string) => string): string {
  const totalMinutes = Math.max(0, Math.round(durationMs / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) {
    const hLabel = hours > 1 ? t('monitor.buildingView.hours') : t('monitor.buildingView.hour');
    if (minutes > 0) {
      const mLabel = minutes > 1 ? t('monitor.buildingView.minutes') : t('monitor.buildingView.minute');
      return `${hours} ${hLabel} ${minutes} ${mLabel}`;
    }
    return `${hours} ${hLabel}`;
  }
  const mLabel = minutes > 1 ? t('monitor.buildingView.minutes') : t('monitor.buildingView.minute');
  return `${minutes} ${mLabel}`;
}

/**
 * True when at least one active profession on the hoist matches the
 * operator trade id. Mirrors trusstor's
 * `Components/Hoists/utils/hoist.utils.ts:7-9`.
 */
export function isOperatorInHoist(hoist: IMergedFloorView<IMinimizedProfession>): boolean {
  return hoist.activeProfessions.some(
    (p) => (p as { tradeId?: string }).tradeId === HOIST_OPERATOR_TRADE_ID,
  );
}
